import { tg } from '@infotech-soe/metaschema';
import classNames from 'classnames';
import React, { MouseEventHandler, useCallback } from 'react';
import { Column } from 'react-table';

import { arrayExt } from '../../../../../framework/language/ArrayExt';

import { SelectedDay, useWorkDaysContext } from './WorkDays';

interface CellValue {
  dayNumber: number;
  dayCode: string;
  style: Record<string, string>;
}

interface Props {
  value: CellValue;
  original: any;
  column: Column & any;
}

function range(from: number, to: number): number[] {
  const delta = Math.sign(to - from);
  return new Array(Math.abs(to - from) + 1)
    .fill(0)
    .map((_v, idx) => from + delta * idx);
}

const CellContent = ({
  value,
}: {
  value: CellValue;
}): React.JSX.Element | null => {
  if (tg.isNothing(value)) {
    return null;
  }
  return (
    <div className="row no-wrap justify-between">
      <div className="col-auto" style={{ width: '24px' }}>
        {value.dayNumber}
      </div>
      <div className="col-auto">{value.dayCode}</div>
    </div>
  );
};

const WorkDaysCell = ({
  value,
  column,
  original,
}: Props): React.JSX.Element => {
  const { selected, setSelected, classes } = useWorkDaysContext();
  const isSelectable = !tg.isNothing(value);
  const select = useCallback(
    (cb: React.SetStateAction<SelectedDay[]>) => {
      if (isSelectable) {
        setSelected(cb);
      }
    },
    [isSelectable, setSelected]
  );
  const isSelected = selected.findIndex(
    s => s.rowId === original.id && s.colId === +column.id
  );

  const clickHandler = useCallback<MouseEventHandler<HTMLDivElement>>(
    e => {
      if (e.ctrlKey) {
        select(s => {
          if (isSelected > -1) {
            const newSelected = s.slice();
            newSelected.splice(isSelected, 1);
            return newSelected;
          }
          return [
            ...s,
            {
              rowId: original.id,
              colId: +column.id,
            },
          ];
        });
      } else if (e.shiftKey) {
        select(s => {
          const a1 = arrayExt(s).at(-1);
          const a2 = { rowId: original.id, colId: +column.id };
          const rect: SelectedDay[] = [];
          for (const rowId of range(a1.rowId, a2.rowId)) {
            for (const colId of range(a1.colId, a2.colId)) {
              rect.push({ rowId, colId });
            }
          }
          return s.slice(0, -1).concat(rect);
        });
      } else {
        select([
          {
            rowId: original.id,
            colId: +column.id,
          },
        ]);
      }
    },
    [column.id, isSelected, original.id, select]
  );

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      style={{
        ...(value ? value.style : {}),
        cursor: isSelectable ? 'pointer' : 'unset',
      }}
      className={classNames(
        classes.dayCell,
        isSelected > -1 && classes.dayCellSelected
      )}
      onClick={clickHandler}
    >
      <CellContent value={value} />
    </div>
  );
};

export default WorkDaysCell;
